import { Text } from "@chakra-ui/react";
import { useContext } from "react";

import { removeNullUndefined } from "@nens/shared-utils";

import { MapResult } from "@/api";
import { ProjectContext } from "@/stores/project-provider";
import { getLabelColor } from "@/utils/get-label-utils";

import { ReportBlock } from "./report-block";

type RenderCategoriesProps = Pick<MapResult, "labelCategories"> & {
  labelColor: string | undefined;
  labelPrimair: string;
};

const LABELS = ["A", "B", "C", "D", "E"];

const LABEL_TO_NUMBER = LABELS.reduce<{ [key: string]: number }>((acc, label, idx) => {
  acc[label] = idx + 1;
  return acc;
}, {});

const NUMBER_TO_LABEL = LABELS.reduce<{ [key: number]: string }>((acc, label, idx) => {
  acc[idx + 1] = label;
  return acc;
}, {});

const useCategories = ({
  isLabelType,
}: {
  isLabelType: (labelType: string) => boolean;
}): RenderCategoriesProps | undefined => {
  let total = 0;
  let amount = 0;

  const { project } = useContext(ProjectContext);
  const maps = project.themes.flatMap((theme) => theme.maps ?? []);
  const filteredMaps = maps.filter(({ labelPrimair, labelType }) => {
    if (!labelType || !isLabelType(labelType)) return false;

    if (!labelPrimair) return false;

    const value = LABEL_TO_NUMBER[labelPrimair];
    if (!value) return true; // No value should not amount to average but should be kept

    total += value;
    amount++;
    return true;
  });

  const average = total / amount;
  const labelPrimair = NUMBER_TO_LABEL[Math.round(average)];

  if (!labelPrimair) return undefined;

  return {
    labelPrimair,
    labelColor: getLabelColor(
      filteredMaps.find(({ labelCategories }) => labelCategories?.length)?.labelCategories,
      labelPrimair
    ),
    labelCategories: removeNullUndefined(
      filteredMaps.map(({ labelPrimair, labelCategories, name, id }) => {
        if (!labelPrimair) return null;
        const theme = project.themes.find((theme) => theme.maps?.some((map) => id === map.id));
        const description = theme ? `${name} (${theme.name})` : name;
        return {
          label: labelPrimair,
          description,
          color: getLabelColor(labelCategories, labelPrimair),
        };
      })
    ),
  };
};

export const ReportPageLandingCategories = ({
  isLabelType,
  description,
}: {
  isLabelType: (labelType: string) => boolean;
  description: React.ReactNode;
}) => {
  const categories = useCategories({ isLabelType });

  if (!categories) return null;
  const { labelColor, labelPrimair, labelCategories } = categories;

  return (
    <>
      <Text flexGrow={1}>{description}</Text>

      <ReportBlock.LabelValue labelColor={labelColor} labelPrimair={labelPrimair} />
      <ReportBlock.LabelCategories labelCategories={labelCategories} />
    </>
  );
};
